/**
 * A rolling window of frame timings for the viewport stats overlay: the
 * interval between frames (→ FPS) and the CPU work each frame's phases did.
 * The GPU's share isn't observable from the page, so it isn't claimed.
 */

export interface FrameTimingSnapshot {
  /** Frames per second over the window (0 before the first measured frame). */
  readonly fps: number;
  /** Mean time between frames, in ms. */
  readonly frameMs: number;
  /** Mean CPU time spent running a frame's phases, in ms. */
  readonly cpuMs: number;
}

const EMPTY: FrameTimingSnapshot = { fps: 0, frameMs: 0, cpuMs: 0 };

export class FrameTimings {
  private readonly intervals: number[] = [];
  private readonly work: number[] = [];

  /** `size` frames: about half a second at 60 fps, steady enough to read. */
  constructor(private readonly size = 30) {}

  record(intervalMs: number, workMs: number): void {
    this.intervals.push(intervalMs);
    this.work.push(workMs);
    if (this.intervals.length > this.size) {
      this.intervals.shift();
      this.work.shift();
    }
  }

  snapshot(): FrameTimingSnapshot {
    if (this.intervals.length === 0) return EMPTY;
    const frameMs = mean(this.intervals);
    return { fps: frameMs > 0 ? 1000 / frameMs : 0, frameMs, cpuMs: mean(this.work) };
  }
}

function mean(values: readonly number[]): number {
  return values.reduce((sum, value) => sum + value, 0) / values.length;
}
